import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { useGuestSession } from '../auth/useGuestSession';
import { ClapperRule, Wordmark } from '../components/Clapper';
import { CheckIcon } from '../components/icons';
import { Banner, Button, Input, Loading, Spinner } from '../components/ui';
import { ApiError, errorMessage } from '../lib/api';
import { dateTime } from '../lib/format';
import { submitterName as storedName } from '../lib/storage';
import { qk } from '../queries/keys';
import { fetchRequestLinkPublic } from '../queries/requestLinks';
import { createUploadTransport } from '../queries/videos';
import { FileUpload } from '../recorder/FileUpload';
import { Recorder } from '../recorder/Recorder';
import { TakePreview } from '../recorder/TakePreview';
import type { RecorderApi } from '../recorder/useRecorder';

/**
 * /r/:token — someone with no Slate account, sending a recording to someone
 * who does.
 *
 * Three paths, equally weighted: record a screen, record audio only, or upload
 * a file they already have. The upload path is not a fallback — it is how an
 * iPhone screen recording gets here. Audio-only is not a fallback either: on a
 * phone it is the one thing that *can* be recorded in the page, because no
 * mobile browser can capture its own screen.
 *
 * When it is done: "sent". No account prompt, no newsletter, no upsell. They
 * did somebody a favour; the page should get out of the way.
 */

function Sent({ requestedBy, onAgain }: { requestedBy: string; onAgain: () => void }) {
  return (
    <div className="mx-auto max-w-md py-16 text-center">
      <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full border border-go/40 bg-go/10 text-go">
        <CheckIcon size={22} />
      </div>
      <h1 className="text-h2 text-chalk">Sent to {requestedBy}</h1>
      <p className="mt-2 text-body text-chalk-dim">
        They have it. Nothing else is needed from you — you can close this tab.
      </p>
      <Button className="mt-6" variant="ghost" onClick={onAgain}>
        Send another
      </Button>
    </div>
  );
}

export function RequestPage({ token }: { token: string }) {
  const [name, setName] = useState(() => storedName.get());
  const [nameError, setNameError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const link = useQuery({
    queryKey: qk.requestLink(token),
    queryFn: () => fetchRequestLinkPublic(token),
    retry: false,
  });

  const guest = useGuestSession(link.isSuccess);
  const transport = useMemo(() => createUploadTransport(token), [token]);

  const trimmedName = name.trim();

  const makeTitle = () => {
    const when = dateTime(new Date().toISOString());
    const label = link.data?.label ?? 'Video';
    return trimmedName ? `${trimmedName} — ${label}` : `${label} — ${when}`;
  };

  const requireName = (): boolean => {
    if (trimmedName.length >= 2) {
      storedName.set(trimmedName);
      setNameError(null);
      return true;
    }
    setNameError('Add your name first so they know who this came from.');
    return false;
  };

  if (link.isLoading) {
    return (
      <Shell>
        <Loading label="Checking this link" />
      </Shell>
    );
  }

  if (link.isError) {
    const notFound = link.error instanceof ApiError && link.error.isNotFound;
    return (
      <Shell>
        <div className="mx-auto max-w-md py-16 text-center">
          <h1 className="text-h3 text-chalk">
            {notFound ? 'This link is closed' : 'That did not load'}
          </h1>
          <p className="mt-2 text-body text-chalk-dim">
            {notFound
              ? link.error instanceof ApiError
                ? link.error.message
                : 'Whoever sent it to you can create a new one.'
              : errorMessage(link.error)}
          </p>
        </div>
      </Shell>
    );
  }

  const details = link.data;
  if (!details) return null;

  if (sent) {
    return (
      <Shell>
        <Sent requestedBy={details.requestedBy} onAgain={() => setSent(false)} />
      </Shell>
    );
  }

  if (guest.error) {
    return (
      <Shell>
        <Banner tone="danger" title="Could not start a session">
          {guest.error}
        </Banner>
      </Shell>
    );
  }

  if (!guest.ready) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2 py-16 text-chalk-faint">
          <Spinner /> Getting things ready…
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mx-auto max-w-3xl space-y-8">
        <div>
          <p className="text-caption uppercase tracking-[0.1em] text-chalk-faint">
            {details.requestedBy} asked for a video
          </p>
          <h1 className="mt-1.5 text-h1 text-chalk">{details.label}</h1>
          <p className="mt-2 text-prose text-chalk-dim">
            Record your screen right here, record audio only, or send a file you already have. No
            account, no app.
          </p>
        </div>

        <div className="max-w-sm">
          <Input
            label="Your name"
            value={name}
            maxLength={80}
            placeholder="So they know who this is from"
            onChange={(event) => {
              setName(event.target.value);
              if (nameError) setNameError(null);
            }}
            onBlur={() => {
              if (trimmedName.length >= 2) storedName.set(trimmedName);
            }}
            error={nameError}
          />
        </div>

        <section className="space-y-4">
          <h2 className="text-h4 text-chalk">Record now</h2>
          <p className="text-body text-chalk-dim">
            Share a screen and talk over it, or pick "Audio only" and just talk.
          </p>
          <div onPointerDownCapture={() => requireName()}>
            <Recorder
              transport={transport}
              makeTitle={makeTitle}
              renderUnsupported={() => null}
              renderPreview={(recorder: RecorderApi) => (
                <SubmissionPreview recorder={recorder} onSent={() => setSent(true)} />
              )}
            />
          </div>
        </section>

        <div className="flex items-center gap-4">
          <div className="h-px flex-1 bg-hairline" />
          <span className="text-caption uppercase tracking-[0.1em] text-chalk-faint">or</span>
          <div className="h-px flex-1 bg-hairline" />
        </div>

        <section className="space-y-4">
          <h2 className="text-h4 text-chalk">Upload a file from your phone or computer</h2>
          <p className="text-body text-chalk-dim">
            A screen recording from an iPhone or Android lands here perfectly, and so does a voice
            memo. So does anything you have already exported.
          </p>
          <div onPointerDownCapture={() => requireName()}>
            <FileUpload
              transport={transport}
              makeTitle={(file) =>
                trimmedName ? `${trimmedName} — ${file.name}` : file.name
              }
              maxBytes={details.maxUploadBytes}
              onUploaded={() => setSent(true)}
            />
          </div>
        </section>
      </div>
    </Shell>
  );
}

/** The post-recording screen for an outsider: confirm and send, nothing else. */
function SubmissionPreview({
  recorder,
  onSent,
}: {
  recorder: RecorderApi;
  onSent: () => void;
}) {
  const failed = recorder.upload.phase === 'failed' || Boolean(recorder.upload.error);
  const [retrying, setRetrying] = useState(false);

  const retry = async () => {
    setRetrying(true);
    try {
      await recorder.retryUpload();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="space-y-4">
      <TakePreview recorder={recorder} />

      {failed ? (
        <Banner
          tone="danger"
          title="It did not finish uploading"
          action={
            <Button onClick={() => void retry()} busy={retrying}>
              Try again
            </Button>
          }
        >
          {recorder.upload.error ?? 'The connection dropped part-way through.'}
        </Banner>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          size="lg"
          disabled={!recorder.result}
          onClick={onSent}
          title={recorder.result ? undefined : 'Waiting for the upload to finish'}
        >
          {recorder.result ? 'Send it' : 'Finishing upload…'}
        </Button>
        <Button variant="ghost" onClick={() => void recorder.discard()}>
          Record it again
        </Button>
      </div>
    </div>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col bg-slate-950">
      <header className="border-b border-hairline">
        <div className="mx-auto flex h-14 w-full max-w-3xl items-center px-4 sm:px-6">
          <Wordmark />
        </div>
        <ClapperRule />
      </header>
      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-8 sm:px-6 sm:py-12">{children}</main>
      <footer className="border-t border-hairline">
        <div className="mx-auto w-full max-w-3xl px-4 py-5 text-caption text-chalk-faint sm:px-6">
          Nothing here creates an account. Your video goes to whoever sent you this link.
        </div>
      </footer>
    </div>
  );
}
